/**
 * Tout le son du jeu, synthetise a l'execution.
 *
 * Aucun fichier audio n'est charge: chaque bruit est construit avec des
 * oscillateurs et du bruit blanc / rose genere une seule fois dans un
 * AudioBuffer. Le graphe est
 *
 *   effets ----\
 *               >-- maitre (volume) -- compresseur doux -- sortie
 *   ambiance --/
 *
 * Chaque son ponctuel occupe une `Voix` qui se deconnecte toute seule quand sa
 * derniere source s'arrete: rien ne fuit. Au-dela de MAX_VOIX voix simultanees
 * les nouvelles demandes sont abandonnees, avec une ligne DEBUG dans le journal.
 */
import { clamp } from '../../shared/sim/alea.ts'
import type { PhaseJour } from '../../shared/sim/temps.ts'
import type { BiomeId, SolId } from '../../shared/types/world.ts'
import { journal } from '../log/journal.ts'

/** Nombre maximum de sons ponctuels joues en meme temps. */
const MAX_VOIX = 24

/** Vitesse du son en m/s: sert au retard des explosions lointaines. */
const VITESSE_SON = 340

/** Duree du fondu enchaine entre deux nappes d'ambiance, en secondes. */
const FONDU_AMBIANCE = 2

/** Valeur plancher des rampes exponentielles (zero est interdit). */
const ZERO = 0.0001

/**
 * Gain global des pas. Les recettes ci-dessous sont ecrites en valeurs
 * relatives; ce facteur les place au-dessus de la nappe d'ambiance (mesure:
 * sans lui, un pas culmine a -38 dBFS alors que le vent tient -32 dBFS RMS).
 */
const GAIN_PAS = 3.2

/** Le lit de vent doit rester sous les effets, jamais l'inverse. */
const NIVEAU_AMBIANCE = 0.6

function hasard(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function unParmi<T>(liste: readonly T[]): T {
  return liste[Math.floor(Math.random() * liste.length)] as T
}

// ---------------------------------------------------------------------- voix

/**
 * Un son ponctuel: un sac de noeuds qui se vide quand la derniere source
 * programmee s'arrete. Une minuterie de secours garantit la liberation meme si
 * `onended` n'arrive jamais.
 */
class Voix {
  readonly ctx: AudioContext
  readonly sortie: AudioNode

  private noeuds: AudioNode[] = []
  private restantes = 0
  private clos = false
  private finPrevue = 0
  private minuterie = 0
  private liberer: () => void

  constructor(ctx: AudioContext, sortie: AudioNode, liberer: () => void) {
    this.ctx = ctx
    this.sortie = sortie
    this.liberer = liberer
  }

  ajouter<T extends AudioNode>(n: T): T {
    this.noeuds.push(n)
    return n
  }

  /** Programme un oscillateur ou un LFO. */
  demarrer(s: OscillatorNode, debut: number, fin: number): void {
    this.brancher(s, debut, fin)
    s.start(Math.max(debut, this.ctx.currentTime))
    s.stop(Math.max(fin, debut + 0.005))
    this.rearmer()
  }

  /** Programme une lecture de tampon, a un decalage aleatoire pres. */
  demarrerTampon(s: AudioBufferSourceNode, debut: number, fin: number, decalage: number): void {
    this.brancher(s, debut, fin)
    s.start(Math.max(debut, this.ctx.currentTime), decalage)
    s.stop(Math.max(fin, debut + 0.005))
    this.rearmer()
  }

  private brancher(s: AudioScheduledSourceNode, debut: number, fin: number): void {
    this.noeuds.push(s)
    this.restantes++
    s.onended = () => {
      this.restantes--
      if (this.restantes <= 0) this.fermer()
    }
    this.finPrevue = Math.max(this.finPrevue, Math.max(fin, debut + 0.005))
  }

  private rearmer(): void {
    if (this.minuterie) window.clearTimeout(this.minuterie)
    const attente = Math.max(0.15, this.finPrevue - this.ctx.currentTime + 0.5)
    this.minuterie = window.setTimeout(() => this.fermer(), attente * 1000)
  }

  fermer(): void {
    if (this.clos) return
    this.clos = true
    if (this.minuterie) window.clearTimeout(this.minuterie)
    this.minuterie = 0
    for (const n of this.noeuds) {
      try {
        n.disconnect()
      } catch {
        // Noeud deja detache: rien a faire.
      }
    }
    this.noeuds.length = 0
    this.liberer()
  }
}

// ------------------------------------------------------------------- recettes

interface Resonance {
  freq: number
  q: number
  duree: number
  gain: number
}

interface RecettePas {
  type: BiquadFilterType
  freq: number
  q: number
  duree: number
  gain: number
  attaque: number
  /** Balayage de la coupure, pour les matieres qui glissent (eau). */
  freqFin?: number
  /** Decoupe en N impulsions: effet craquant (neige). */
  impulsions?: number
  /** Corps resonant, pour les matieres qui sonnent (roche, bois, tuile). */
  reso?: Resonance
  /** Petit son tonal par-dessus (goutte d'eau). */
  goutte?: { debut: number; fin: number; gain: number; duree: number }
}

/**
 * Un timbre par matiere. Les valeurs sont volontairement tres separees: on
 * doit reconnaitre le sol a l'oreille, les yeux fermes.
 */
const PAS: Record<SolId, RecettePas> = {
  sable: { type: 'lowpass', freq: 900, q: 0.7, duree: 0.09, gain: 0.42, attaque: 0.007 },
  herbe: { type: 'bandpass', freq: 1400, q: 1.1, duree: 0.11, gain: 0.36, attaque: 0.005 },
  terre: { type: 'lowpass', freq: 520, q: 0.8, duree: 0.1, gain: 0.67, attaque: 0.006 },
  neige: { type: 'highpass', freq: 2600, q: 0.8, duree: 0.13, gain: 0.1, attaque: 0.001, impulsions: 4 },
  roche: {
    type: 'highpass',
    freq: 3000,
    q: 0.7,
    duree: 0.03,
    gain: 0.18,
    attaque: 0.001,
    reso: { freq: 2200, q: 9, duree: 0.14, gain: 0.058 },
  },
  bois: {
    type: 'lowpass',
    freq: 1600,
    q: 0.7,
    duree: 0.05,
    gain: 0.48,
    attaque: 0.001,
    reso: { freq: 380, q: 7, duree: 0.18, gain: 0.29 },
  },
  eau: {
    type: 'bandpass',
    freq: 420,
    q: 1.6,
    duree: 0.26,
    gain: 0.1,
    attaque: 0.012,
    freqFin: 1900,
    goutte: { debut: 900, fin: 300, gain: 0.023, duree: 0.14 },
  },
  tuile: {
    type: 'bandpass',
    freq: 3800,
    q: 4,
    duree: 0.05,
    gain: 0.52,
    attaque: 0.001,
    reso: { freq: 5200, q: 10, duree: 0.1, gain: 0.16 },
  },
  /*
   * Le cristal: la roche, mais qui TINTE. C'est tout l'interet de lui avoir
   * donne un `SolId` a lui plutot que de le ranger dans `roche` — un canyon de
   * cristal doit s'entendre avant de se voir, et la resonance longue et haute
   * est ce qui le dit.
   */
  cristal: {
    type: 'highpass',
    freq: 3400,
    q: 0.7,
    duree: 0.035,
    gain: 0.16,
    attaque: 0.001,
    reso: { freq: 4400, q: 16, duree: 0.42, gain: 0.075 },
  },
}

interface RecetteNappe {
  type: BiquadFilterType
  freq: number
  q: number
  niveau: number
  /** Vitesse de lecture du bruit: change la couleur globale du vent. */
  taux: number
  /** Balancement de la coupure du filtre, en hertz. */
  lfoFiltre: { taux: number; profondeur: number }
  /** Respiration du niveau, en fraction du niveau de base. */
  lfoAmpli: { taux: number; profondeur: number }
  /** Couche tonale ajoutee: sifflement du glacier, rumeur du desert. */
  couche?: { type: OscillatorType; freq: number; gain: number; vibratoTaux: number; vibratoCents: number }
}

const NAPPES: Record<BiomeId, RecetteNappe> = {
  // Vent aigu et sifflant, avec une note qui traine dans les cretes.
  glacier: {
    type: 'highpass',
    freq: 2400,
    q: 0.8,
    niveau: 0.1,
    taux: 1.4,
    lfoFiltre: { taux: 0.13, profondeur: 900 },
    lfoAmpli: { taux: 0.11, profondeur: 0.5 },
    couche: { type: 'sine', freq: 1150, gain: 0.014, vibratoTaux: 0.21, vibratoCents: 70 },
  },
  // Vent chaud et sourd, presque sans aigus, avec une rumeur basse.
  desert: {
    type: 'lowpass',
    freq: 380,
    q: 0.7,
    niveau: 0.14,
    taux: 0.75,
    lfoFiltre: { taux: 0.09, profondeur: 130 },
    lfoAmpli: { taux: 0.07, profondeur: 0.45 },
    couche: { type: 'sine', freq: 88, gain: 0.022, vibratoTaux: 0.05, vibratoCents: 30 },
  },
  // Frottement de feuilles: bande medium-aigue qui bouge sans arret.
  foret: {
    type: 'bandpass',
    freq: 2100,
    q: 0.8,
    niveau: 0.095,
    taux: 1.1,
    lfoFiltre: { taux: 0.23, profondeur: 750 },
    lfoAmpli: { taux: 0.17, profondeur: 0.55 },
  },
  // Vagues lentes: la respiration d'amplitude fait le va-et-vient.
  plage: {
    type: 'lowpass',
    freq: 900,
    q: 0.9,
    niveau: 0.15,
    taux: 0.9,
    lfoFiltre: { taux: 0.11, profondeur: 550 },
    lfoAmpli: { taux: 0.11, profondeur: 0.8 },
  },
  // Rafales: creux longs et bouffees fortes.
  montagne: {
    type: 'bandpass',
    freq: 700,
    q: 0.6,
    niveau: 0.13,
    taux: 1,
    lfoFiltre: { taux: 0.07, profondeur: 520 },
    lfoAmpli: { taux: 0.065, profondeur: 0.85 },
  },
  // Le canyon de cristal: un vent etroit, et une note cristalline qui traine
  // dans les parois. C'est cette couche tonale qui le distingue de la montagne.
  cristal: {
    type: 'bandpass',
    freq: 1900,
    q: 1.4,
    niveau: 0.1,
    taux: 1.15,
    lfoFiltre: { taux: 0.1, profondeur: 700 },
    lfoAmpli: { taux: 0.09, profondeur: 0.6 },
    couche: { type: 'triangle', freq: 1760, gain: 0.012, vibratoTaux: 0.17, vibratoCents: 45 },
  },
  // La jungle: plus grasse et plus basse que la foret, elle bouge tout le temps.
  jungle: {
    type: 'bandpass',
    freq: 1500,
    q: 0.75,
    niveau: 0.12,
    taux: 1.25,
    lfoFiltre: { taux: 0.31, profondeur: 900 },
    lfoAmpli: { taux: 0.22, profondeur: 0.6 },
  },
  // La ferme: un souffle large et calme sur les champs, presque rien.
  ferme: {
    type: 'lowpass',
    freq: 1100,
    q: 0.7,
    niveau: 0.085,
    taux: 0.85,
    lfoFiltre: { taux: 0.08, profondeur: 340 },
    lfoAmpli: { taux: 0.09, profondeur: 0.5 },
  },
}

/** Le monde est plus discret la nuit et aux heures pales. */
const NIVEAU_PHASE: Record<PhaseJour, number> = { aube: 0.9, jour: 1, soir: 0.9, nuit: 0.75 }

interface Nappe {
  /** Gain externe, reserve au fondu enchaine. */
  externe: GainNode
  niveau: number
  noeuds: AudioNode[]
  sources: AudioScheduledSourceNode[]
}

interface Courbe {
  t: number
  f: number
}

// --------------------------------------------------------------------- classe

export class Audio {
  private ctx: AudioContext | null = null
  private maitre: GainNode | null = null
  private busEffets: GainNode | null = null
  private busAmbiance: GainNode | null = null
  private bufBlanc: AudioBuffer | null = null
  private bufRose: AudioBuffer | null = null

  private voix = 0
  private sourd = false
  private niveau = 0.8
  private demarrage: Promise<void> | null = null

  private nappe: Nappe | null = null
  private sortantes: { nappe: Nappe; fin: number }[] = []
  private biome: BiomeId = 'foret'
  private phase: PhaseJour = 'jour'
  private prochainEvenement = 5
  private piedGauche = false

  /** Vrai des que le contexte audio existe et fonctionne. */
  get pret(): boolean {
    return this.ctx !== null && this.ctx.state !== 'closed'
  }

  get estMuet(): boolean {
    return this.sourd
  }

  // ------------------------------------------------------------------ init

  /**
   * A appeler depuis un gestionnaire de clic: les navigateurs refusent de
   * creer un AudioContext avant un geste de l'utilisateur. Idempotent.
   */
  async init(): Promise<void> {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') {
        try {
          await this.ctx.resume()
        } catch (e) {
          journal.error('audio', 'reprise du contexte impossible', String(e))
        }
      }
      return
    }
    if (this.demarrage) return this.demarrage
    this.demarrage = this.creerContexte()
    return this.demarrage
  }

  private async creerContexte(): Promise<void> {
    try {
      const ctx = new AudioContext({ latencyHint: 'interactive' })
      if (ctx.state === 'suspended') await ctx.resume()

      // Compresseur doux en bout de chaine: les bouquets de feux montent haut,
      // on veut qu'ils tapent sans saturer ni ecraser le reste.
      const comp = ctx.createDynamicsCompressor()
      comp.threshold.value = -16
      comp.knee.value = 24
      comp.ratio.value = 3.5
      comp.attack.value = 0.006
      comp.release.value = 0.22
      comp.connect(ctx.destination)

      const maitre = ctx.createGain()
      maitre.gain.value = this.sourd ? ZERO : this.niveau
      maitre.connect(comp)

      const effets = ctx.createGain()
      effets.gain.value = 1
      effets.connect(maitre)

      const ambiance = ctx.createGain()
      ambiance.gain.value = NIVEAU_AMBIANCE
      ambiance.connect(maitre)

      this.ctx = ctx
      this.maitre = maitre
      this.busEffets = effets
      this.busAmbiance = ambiance
      this.bufBlanc = this.tamponBlanc(ctx, 2)
      this.bufRose = this.tamponRose(ctx, 8)

      this.appliquerNappe(0.6)
      journal.info('audio', `initialise a ${ctx.sampleRate} Hz`, { biome: this.biome, phase: this.phase })
    } catch (e) {
      journal.error('audio', 'initialisation impossible', String(e))
      this.ctx = null
    }
  }

  /** Bruit blanc, boucle sans clic parce qu'on le lit a un endroit au hasard. */
  private tamponBlanc(ctx: AudioContext, secondes: number): AudioBuffer {
    const n = Math.floor(ctx.sampleRate * secondes)
    const buf = ctx.createBuffer(1, n, ctx.sampleRate)
    const d = buf.getChannelData(0)
    for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1
    return buf
  }

  /** Bruit rose (filtre de Paul Kellet): base de tous les vents. */
  private tamponRose(ctx: AudioContext, secondes: number): AudioBuffer {
    const n = Math.floor(ctx.sampleRate * secondes)
    const buf = ctx.createBuffer(1, n, ctx.sampleRate)
    const d = buf.getChannelData(0)
    let b0 = 0
    let b1 = 0
    let b2 = 0
    let b3 = 0
    let b4 = 0
    let b5 = 0
    let b6 = 0
    for (let i = 0; i < n; i++) {
      const w = Math.random() * 2 - 1
      b0 = 0.99886 * b0 + w * 0.0555179
      b1 = 0.99332 * b1 + w * 0.0750759
      b2 = 0.969 * b2 + w * 0.153852
      b3 = 0.8665 * b3 + w * 0.3104856
      b4 = 0.55 * b4 + w * 0.5329522
      b5 = -0.7616 * b5 - w * 0.016898
      d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.09
      b6 = w * 0.115926
    }
    return buf
  }

  // ---------------------------------------------------------------- reglages

  muet(actif: boolean): void {
    if (this.sourd === actif) return
    this.sourd = actif
    const ctx = this.ctx
    const m = this.maitre
    if (ctx && m) {
      const t = ctx.currentTime
      m.gain.cancelScheduledValues(t)
      m.gain.setValueAtTime(Math.max(ZERO, m.gain.value), t)
      m.gain.linearRampToValueAtTime(actif ? ZERO : this.niveau, t + 0.15)
    }
    journal.info('audio', actif ? 'son coupe' : 'son remis')
  }

  volume(v: number): void {
    this.niveau = clamp(v, 0, 1)
    const ctx = this.ctx
    const m = this.maitre
    if (!ctx || !m || this.sourd) return
    const t = ctx.currentTime
    m.gain.cancelScheduledValues(t)
    m.gain.setValueAtTime(Math.max(ZERO, m.gain.value), t)
    m.gain.linearRampToValueAtTime(Math.max(ZERO, this.niveau), t + 0.08)
  }

  // ------------------------------------------------------------------- voix

  private ouvrir(sortie?: AudioNode | null): Voix | null {
    const ctx = this.ctx
    const bus = sortie ?? this.busEffets
    if (!ctx || !bus || this.sourd) return null
    if (this.voix >= MAX_VOIX) {
      journal.debug('audio', `${MAX_VOIX} voix deja actives, son abandonne`)
      return null
    }
    this.voix++
    return new Voix(ctx, bus, () => {
      this.voix = Math.max(0, this.voix - 1)
    })
  }

  // -------------------------------------------------------------- primitives

  /** Grain de bruit filtre: la brique de tous les impacts, pas et souffles. */
  private grain(o: {
    v: Voix
    debut: number
    duree: number
    gain: number
    type: BiquadFilterType
    freq: number
    freqFin?: number
    q?: number
    attaque?: number
    pan?: number
    impulsions?: number
    rose?: boolean
    sortie?: AudioNode
  }): void {
    const v = o.v
    const ctx = v.ctx
    const buf = o.rose ? this.bufRose : this.bufBlanc
    if (!buf) return

    const src = ctx.createBufferSource()
    src.buffer = buf
    src.loop = true

    const filtre = v.ajouter(ctx.createBiquadFilter())
    filtre.type = o.type
    filtre.Q.value = o.q ?? 1
    filtre.frequency.setValueAtTime(Math.max(20, o.freq), o.debut)
    if (o.freqFin !== undefined) {
      filtre.frequency.exponentialRampToValueAtTime(Math.max(20, o.freqFin), o.debut + o.duree)
    }

    const g = v.ajouter(ctx.createGain())
    const att = Math.max(0.0008, o.attaque ?? 0.004)
    const n = Math.max(1, Math.floor(o.impulsions ?? 1))
    if (n === 1) {
      g.gain.setValueAtTime(ZERO, o.debut)
      g.gain.exponentialRampToValueAtTime(Math.max(ZERO, o.gain), o.debut + att)
      g.gain.exponentialRampToValueAtTime(ZERO, o.debut + o.duree)
    } else {
      // Decoupe en impulsions: le craquement de la neige, le froissement des
      // feuilles, le stridulement du grillon. Chaque creux redescend avant
      // l'impulsion suivante pour garder les evenements bien ordonnes.
      g.gain.setValueAtTime(ZERO, o.debut)
      const pas = o.duree / n
      for (let i = 0; i < n; i++) {
        const t0 = o.debut + i * pas + Math.random() * pas * 0.3
        const crete = Math.max(ZERO, o.gain * hasard(0.55, 1.15))
        g.gain.setValueAtTime(ZERO, t0)
        g.gain.exponentialRampToValueAtTime(crete, t0 + 0.0015)
        g.gain.exponentialRampToValueAtTime(ZERO, t0 + pas * 0.5)
      }
    }

    src.connect(filtre)
    let dernier: AudioNode = filtre
    dernier.connect(g)
    dernier = g
    if (o.pan !== undefined) {
      const p = v.ajouter(ctx.createStereoPanner())
      p.pan.value = clamp(o.pan, -1, 1)
      dernier.connect(p)
      dernier = p
    }
    dernier.connect(o.sortie ?? v.sortie)
    v.demarrerTampon(src, o.debut, o.debut + o.duree + 0.03, Math.random() * 1.5)
  }

  /** Corps resonant excite par du bruit: ce qui donne sa matiere a un impact. */
  private resonance(v: Voix, debut: number, r: Resonance, sortie?: AudioNode): void {
    this.grain({
      v,
      debut,
      duree: r.duree,
      gain: r.gain,
      type: 'bandpass',
      freq: r.freq,
      q: r.q,
      attaque: 0.001,
      ...(sortie ? { sortie } : {}),
    })
  }

  /** Note ou glissando: la brique de tous les sons tonals. */
  private oscil(o: {
    v: Voix
    debut: number
    duree: number
    gain: number
    courbe: Courbe[]
    type?: OscillatorType
    attaque?: number
    coupure?: number
    vibrato?: { taux: number; cents: number }
    sortie?: AudioNode
  }): void {
    const v = o.v
    const ctx = v.ctx
    const premier = o.courbe[0]
    if (!premier) return

    const osc = ctx.createOscillator()
    osc.type = o.type ?? 'sine'
    osc.frequency.setValueAtTime(Math.max(10, premier.f), o.debut)
    for (let i = 1; i < o.courbe.length; i++) {
      const p = o.courbe[i]!
      osc.frequency.exponentialRampToValueAtTime(Math.max(10, p.f), o.debut + p.t)
    }

    if (o.vibrato) {
      const lfo = ctx.createOscillator()
      lfo.frequency.value = o.vibrato.taux
      const prof = v.ajouter(ctx.createGain())
      prof.gain.value = o.vibrato.cents
      lfo.connect(prof)
      prof.connect(osc.detune)
      v.demarrer(lfo, o.debut, o.debut + o.duree + 0.05)
    }

    const g = v.ajouter(ctx.createGain())
    const att = Math.max(0.002, o.attaque ?? 0.01)
    g.gain.setValueAtTime(ZERO, o.debut)
    g.gain.exponentialRampToValueAtTime(Math.max(ZERO, o.gain), o.debut + att)
    g.gain.exponentialRampToValueAtTime(ZERO, o.debut + o.duree)

    let dernier: AudioNode = osc
    if (o.coupure !== undefined) {
      const f = v.ajouter(ctx.createBiquadFilter())
      f.type = 'lowpass'
      f.frequency.value = o.coupure
      f.Q.value = 0.7
      dernier.connect(f)
      dernier = f
    }
    dernier.connect(g)
    g.connect(o.sortie ?? v.sortie)
    v.demarrer(osc, o.debut, o.debut + o.duree + 0.03)
  }

  // -------------------------------------------------------------------- pas

  pas(sol: SolId): void {
    const v = this.ouvrir()
    if (!v) return
    const r = PAS[sol]
    const t = v.ctx.currentTime + 0.001
    // Variation de hauteur et de volume: sans elle, marcher fait mitraillette.
    const h = hasard(0.88, 1.15)
    const gv = hasard(0.8, 1.15)
    this.piedGauche = !this.piedGauche
    const pan = this.piedGauche ? -0.16 : 0.16

    this.grain({
      v,
      debut: t,
      duree: r.duree,
      gain: r.gain * gv * GAIN_PAS,
      type: r.type,
      freq: r.freq * h,
      q: r.q,
      attaque: r.attaque,
      pan,
      ...(r.freqFin !== undefined ? { freqFin: r.freqFin * h } : {}),
      ...(r.impulsions !== undefined ? { impulsions: r.impulsions } : {}),
    })
    if (r.reso) {
      this.resonance(v, t, { ...r.reso, freq: r.reso.freq * h, gain: r.reso.gain * gv * GAIN_PAS })
    }
    if (r.goutte) {
      this.oscil({
        v,
        debut: t + 0.05,
        duree: r.goutte.duree,
        gain: r.goutte.gain * gv * GAIN_PAS,
        courbe: [
          { t: 0, f: r.goutte.debut * h },
          { t: r.goutte.duree, f: r.goutte.fin * h },
        ],
        attaque: 0.004,
      })
    }
  }

  // ------------------------------------------------------------------ outils

  coup(action: 'couper' | 'miner' | 'cueillir'): void {
    const v = this.ouvrir()
    if (!v) return
    const t = v.ctx.currentTime + 0.001
    const h = hasard(0.92, 1.09)

    if (action === 'couper') {
      // Hache dans le bois: masse sourde, corps qui sonne, eclat de fibre.
      this.grain({ v, debut: t, duree: 0.09, gain: 0.6, type: 'lowpass', freq: 240 * h, q: 0.8, attaque: 0.001 })
      this.resonance(v, t, { freq: 500 * h, q: 8, duree: 0.21, gain: 0.24 })
      this.grain({ v, debut: t + 0.004, duree: 0.06, gain: 0.25, type: 'highpass', freq: 2300 * h, attaque: 0.001 })
      return
    }
    if (action === 'miner') {
      // Pioche sur la pierre: attaque tres aigue et deux resonances metalliques.
      this.grain({ v, debut: t, duree: 0.03, gain: 0.4, type: 'highpass', freq: 3500 * h, attaque: 0.001 })
      this.resonance(v, t, { freq: 1800 * h, q: 13, duree: 0.23, gain: 0.17 })
      this.resonance(v, t + 0.006, { freq: 3300 * h, q: 15, duree: 0.16, gain: 0.11 })
      this.grain({ v, debut: t, duree: 0.08, gain: 0.32, type: 'lowpass', freq: 190, q: 0.8, attaque: 0.002 })
      return
    }
    // Cueillette: froissement de feuilles, plusieurs bouffees irregulieres.
    this.grain({
      v,
      debut: t,
      duree: 0.34,
      gain: 0.09,
      type: 'bandpass',
      freq: 2600 * h,
      q: 1,
      attaque: 0.006,
      impulsions: 5,
    })
    this.grain({ v, debut: t + 0.02, duree: 0.2, gain: 0.07, type: 'highpass', freq: 4200, attaque: 0.02 })
  }

  ressourceEpuisee(action: 'couper' | 'miner' | 'cueillir'): void {
    const v = this.ouvrir()
    if (!v) return
    const t = v.ctx.currentTime + 0.001

    if (action === 'couper') {
      // L'arbre qui tombe: craquement, longue descente de feuillage, impact.
      this.grain({ v, debut: t, duree: 0.2, gain: 0.28, type: 'highpass', freq: 1500, attaque: 0.002, impulsions: 3 })
      this.resonance(v, t, { freq: 320, q: 6, duree: 0.34, gain: 0.14 })
      this.grain({
        v,
        debut: t + 0.1,
        duree: 1.15,
        gain: 0.2,
        type: 'bandpass',
        freq: 1500,
        freqFin: 380,
        q: 0.9,
        attaque: 0.15,
      })
      this.grain({ v, debut: t + 0.95, duree: 0.32, gain: 0.36, type: 'lowpass', freq: 220, q: 0.9, attaque: 0.004 })
      this.oscil({
        v,
        debut: t + 0.95,
        duree: 0.55,
        gain: 0.42,
        courbe: [
          { t: 0, f: 82 },
          { t: 0.55, f: 36 },
        ],
        attaque: 0.006,
        coupure: 300,
      })
      return
    }
    if (action === 'miner') {
      // Le rocher qui s'effondre: grondement, basse qui plonge, eboulis.
      this.grain({
        v,
        debut: t,
        duree: 1,
        gain: 0.3,
        type: 'lowpass',
        freq: 700,
        freqFin: 260,
        q: 0.9,
        attaque: 0.01,
        rose: true,
      })
      this.oscil({
        v,
        debut: t,
        duree: 0.7,
        gain: 0.4,
        courbe: [
          { t: 0, f: 72 },
          { t: 0.7, f: 30 },
        ],
        attaque: 0.005,
        coupure: 260,
      })
      for (let i = 0; i < 5; i++) {
        const d = t + 0.08 + i * 0.14 + hasard(0, 0.08)
        this.resonance(v, d, { freq: hasard(1500, 3200), q: 12, duree: hasard(0.07, 0.14), gain: 0.05 })
      }
      return
    }
    // Le buisson depouille: froissement plus long qui retombe.
    this.grain({
      v,
      debut: t,
      duree: 0.55,
      gain: 0.19,
      type: 'bandpass',
      freq: 2400,
      freqFin: 900,
      q: 1,
      attaque: 0.01,
      impulsions: 6,
    })
    this.oscil({
      v,
      debut: t + 0.3,
      duree: 0.22,
      gain: 0.07,
      courbe: [
        { t: 0, f: 320 },
        { t: 0.22, f: 190 },
      ],
      attaque: 0.01,
      coupure: 900,
    })
  }

  // ------------------------------------------------------------------ besoins

  manger(): void {
    const v = this.ouvrir()
    if (!v) return
    const t = v.ctx.currentTime + 0.001
    for (let i = 0; i < 3; i++) {
      const d = t + i * 0.17
      const h = hasard(0.9, 1.12)
      this.grain({ v, debut: d, duree: 0.1, gain: 0.26, type: 'lowpass', freq: 330 * h, freqFin: 170 * h, q: 1.4, attaque: 0.006 })
      this.oscil({
        v,
        debut: d,
        duree: 0.09,
        gain: 0.05,
        courbe: [
          { t: 0, f: 190 * h },
          { t: 0.09, f: 130 * h },
        ],
        attaque: 0.008,
        coupure: 600,
      })
    }
  }

  boire(): void {
    const v = this.ouvrir()
    if (!v) return
    const t = v.ctx.currentTime + 0.001
    // Trois gorgees: la hauteur monte a mesure que le recipient se vide.
    const hauteurs = [260, 300, 348]
    for (let i = 0; i < hauteurs.length; i++) {
      const d = t + i * 0.16
      const f = hauteurs[i]! * hasard(0.96, 1.05)
      this.oscil({
        v,
        debut: d,
        duree: 0.11,
        gain: 0.12,
        courbe: [
          { t: 0, f },
          { t: 0.11, f: f * 0.6 },
        ],
        attaque: 0.006,
        coupure: 1200,
      })
      this.grain({ v, debut: d, duree: 0.08, gain: 0.1, type: 'lowpass', freq: 520, q: 1.2, attaque: 0.005 })
    }
  }

  // ----------------------------------------------------------------- interface

  ramasser(): void {
    const v = this.ouvrir()
    if (!v) return
    const t = v.ctx.currentTime + 0.001
    // Arpege ascendant: sol - si - re - sol, court et satisfaisant.
    const notes = [783.99, 987.77, 1174.66, 1567.98]
    for (let i = 0; i < notes.length; i++) {
      this.oscil({
        v,
        debut: t + i * 0.055,
        duree: 0.17,
        gain: 0.1,
        type: 'triangle',
        courbe: [{ t: 0, f: notes[i]! }],
        attaque: 0.005,
        coupure: 5200,
      })
    }
  }

  construire(): void {
    const v = this.ouvrir()
    if (!v) return
    const t = v.ctx.currentTime + 0.001
    // Deux cognements de bois...
    for (let i = 0; i < 2; i++) {
      const d = t + i * 0.115
      this.grain({ v, debut: d, duree: 0.05, gain: 0.2, type: 'lowpass', freq: 1300, attaque: 0.001 })
      this.resonance(v, d, { freq: 340 * (i === 0 ? 1 : 1.18), q: 7, duree: 0.16, gain: 0.1 })
    }
    // ...puis un petit accord majeur pour dire que c'est pose.
    const accord = [523.25, 659.25, 783.99]
    for (const f of accord) {
      this.oscil({
        v,
        debut: t + 0.17,
        duree: 0.45,
        gain: 0.065,
        type: 'triangle',
        courbe: [{ t: 0, f }],
        attaque: 0.012,
        coupure: 4200,
      })
    }
  }

  erreurInterface(): void {
    const v = this.ouvrir()
    if (!v) return
    const ctx = v.ctx
    const t = ctx.currentTime + 0.001

    const osc = ctx.createOscillator()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(168, t)
    osc.frequency.exponentialRampToValueAtTime(118, t + 0.15)

    const f = v.ajouter(ctx.createBiquadFilter())
    f.type = 'lowpass'
    f.frequency.value = 720
    f.Q.value = 0.9

    const g = v.ajouter(ctx.createGain())
    g.gain.setValueAtTime(ZERO, t)
    g.gain.exponentialRampToValueAtTime(0.1, t + 0.012)
    g.gain.exponentialRampToValueAtTime(ZERO, t + 0.16)

    // Un tremblement rapide sur le gain donne le "bzz" sans agresser.
    const lfo = ctx.createOscillator()
    lfo.frequency.value = 30
    const prof = v.ajouter(ctx.createGain())
    prof.gain.value = 0.045
    lfo.connect(prof)
    prof.connect(g.gain)

    osc.connect(f)
    f.connect(g)
    g.connect(v.sortie)
    v.demarrer(osc, t, t + 0.18)
    v.demarrer(lfo, t, t + 0.18)
  }

  clic(): void {
    const v = this.ouvrir()
    if (!v) return
    const t = v.ctx.currentTime + 0.001
    this.grain({ v, debut: t, duree: 0.022, gain: 0.24, type: 'bandpass', freq: 2200, q: 2, attaque: 0.001 })
    this.oscil({ v, debut: t, duree: 0.035, gain: 0.1, courbe: [{ t: 0, f: 1400 }], attaque: 0.002 })
  }

  // -------------------------------------------------------------------- feux

  /** Sifflement de la fusee qui monte: une bande qui glisse vers l'aigu. */
  feuLancement(): void {
    const v = this.ouvrir()
    if (!v) return
    const ctx = v.ctx
    const t = ctx.currentTime + 0.001
    const duree = 1.2

    const src = ctx.createBufferSource()
    const buf = this.bufBlanc
    if (!buf) return
    src.buffer = buf
    src.loop = true

    const f = v.ajouter(ctx.createBiquadFilter())
    f.type = 'bandpass'
    f.Q.value = 5.5
    f.frequency.setValueAtTime(330, t)
    f.frequency.exponentialRampToValueAtTime(2400, t + duree)

    // Battement rapide de la coupure: le souffle papillonne au lieu de siffler
    // comme une sirene.
    const lfo = ctx.createOscillator()
    lfo.frequency.value = 7.5
    const prof = v.ajouter(ctx.createGain())
    prof.gain.value = 130
    lfo.connect(prof)
    prof.connect(f.frequency)

    const g = v.ajouter(ctx.createGain())
    g.gain.setValueAtTime(ZERO, t)
    g.gain.exponentialRampToValueAtTime(0.17, t + 0.28)
    g.gain.setValueAtTime(0.17, t + duree - 0.12)
    g.gain.exponentialRampToValueAtTime(ZERO, t + duree)

    src.connect(f)
    f.connect(g)
    g.connect(v.sortie)
    v.demarrerTampon(src, t, t + duree + 0.02, Math.random() * 1.5)
    v.demarrer(lfo, t, t + duree + 0.02)
  }

  /**
   * Explosion. Le son part en RETARD de `distance / 340` secondes: la lumiere
   * arrive d'abord, le bruit suit, et l'ecart se voit des la centaine de metres.
   * Plus c'est loin, plus c'est grave, sourd, long et faible: de proche
   * ("CRAC" sec) a lointain ("boum" mou).
   */
  feuExplosion(distance: number): void {
    const v = this.ouvrir()
    if (!v) return
    const d = Math.max(0, distance)
    const t = v.ctx.currentTime + d / VITESSE_SON

    // 1 tout pres, 0 a l'autre bout du monde.
    const proche = clamp(1 - d / 280, 0, 1)
    // L'air avale les aigus: la coupure s'ecroule avec la distance.
    const coupure = clamp(17000 * Math.exp(-d / 85), 260, 17000)
    const niveau = clamp(1 / (1 + d / 55), 0.05, 1)

    // Couche 1 - le claquement. C'est lui qui fait le "CRAC", et c'est lui qui
    // disparait le plus vite quand on s'eloigne.
    this.grain({
      v,
      debut: t,
      duree: 0.06,
      gain: 0.55 * niveau * (0.2 + 0.8 * proche),
      type: 'lowpass',
      freq: coupure,
      q: 0.8,
      attaque: 0.001,
    })
    this.grain({
      v,
      debut: t,
      duree: 0.045,
      gain: 0.3 * niveau * proche * proche,
      type: 'highpass',
      freq: Math.min(2600, coupure * 0.6),
      attaque: 0.0008,
    })

    // Couche 2 - le grondement: deux sinus qui plongent. Ce qui reste de loin.
    const dureeBasse = 0.7 + (1 - proche) * 0.8
    this.oscil({
      v,
      debut: t,
      duree: dureeBasse,
      gain: 0.5 * niveau,
      courbe: [
        { t: 0, f: 60 + 40 * proche },
        { t: dureeBasse, f: 26 },
      ],
      attaque: 0.006,
      coupure: Math.min(400, coupure),
    })
    this.oscil({
      v,
      debut: t + 0.02,
      duree: dureeBasse * 0.8,
      gain: 0.34 * niveau,
      courbe: [
        { t: 0, f: 44 },
        { t: dureeBasse * 0.8, f: 20 },
      ],
      attaque: 0.01,
      coupure: 200,
    })

    // Couche 3 - la queue de bruit qui traine, d'autant plus longue que c'est
    // loin (l'echo du paysage).
    const dureeQueue = 0.9 + (1 - proche) * 1.3
    this.grain({
      v,
      debut: t + 0.01,
      duree: dureeQueue,
      gain: 0.3 * niveau,
      type: 'lowpass',
      freq: coupure * 0.55,
      freqFin: Math.max(120, coupure * 0.16),
      q: 0.8,
      attaque: 0.012,
      rose: true,
    })

    // Bonus de proximite: le crepitement des etincelles.
    if (d < 95) {
      this.grain({
        v,
        debut: t + 0.08,
        duree: 0.7,
        gain: 0.09 * niveau * proche,
        type: 'bandpass',
        freq: 4200,
        freqFin: 2600,
        q: 3,
        attaque: 0.002,
        impulsions: 14,
      })
    }
  }

  // ---------------------------------------------------------------- ambiance

  /**
   * Nappe de fond continue. Le timbre depend du biome, le niveau de la phase,
   * et le changement passe par un fondu enchaine de deux secondes.
   */
  ambiance(biome: BiomeId, phase: PhaseJour): void {
    const change = biome !== this.biome || phase !== this.phase
    this.biome = biome
    this.phase = phase
    if (!this.ctx || !this.busAmbiance) return
    if (change || !this.nappe) this.appliquerNappe(FONDU_AMBIANCE)
  }

  private appliquerNappe(duree: number): void {
    const ctx = this.ctx
    const bus = this.busAmbiance
    if (!ctx || !bus) return
    const t = ctx.currentTime
    const neuve = this.construireNappe(t)

    neuve.externe.gain.cancelScheduledValues(t)
    neuve.externe.gain.setValueAtTime(ZERO, t)
    neuve.externe.gain.linearRampToValueAtTime(1, t + duree)

    if (this.nappe) {
      const vieille = this.nappe
      const g = vieille.externe.gain
      g.cancelScheduledValues(t)
      g.setValueAtTime(Math.max(ZERO, g.value), t)
      g.linearRampToValueAtTime(ZERO, t + duree)
      this.sortantes.push({ nappe: vieille, fin: t + duree + 0.25 })
    }
    this.nappe = neuve
  }

  private construireNappe(t: number): Nappe {
    const ctx = this.ctx as AudioContext
    const bus = this.busAmbiance as AudioNode
    const r = NAPPES[this.biome]
    const niveau = r.niveau * NIVEAU_PHASE[this.phase]
    const noeuds: AudioNode[] = []
    const sources: AudioScheduledSourceNode[] = []

    // Gain externe: c'est lui, et lui seul, que le fondu enchaine automatise.
    const externe = ctx.createGain()
    externe.gain.value = ZERO
    externe.connect(bus)
    noeuds.push(externe)

    // Gain interne: respiration du vent (rafales, vagues).
    const interne = ctx.createGain()
    interne.gain.value = niveau
    interne.connect(externe)
    noeuds.push(interne)

    const filtre = ctx.createBiquadFilter()
    filtre.type = r.type
    filtre.frequency.value = r.freq
    filtre.Q.value = r.q
    filtre.connect(interne)
    noeuds.push(filtre)

    const vent = ctx.createBufferSource()
    vent.buffer = this.bufRose
    vent.loop = true
    vent.playbackRate.value = r.taux
    vent.connect(filtre)
    vent.start(t, Math.random() * 4)
    sources.push(vent)

    const lfoF = ctx.createOscillator()
    lfoF.frequency.value = r.lfoFiltre.taux
    const profF = ctx.createGain()
    profF.gain.value = r.lfoFiltre.profondeur
    lfoF.connect(profF)
    profF.connect(filtre.frequency)
    lfoF.start(t)
    sources.push(lfoF)
    noeuds.push(profF)

    const lfoA = ctx.createOscillator()
    lfoA.frequency.value = r.lfoAmpli.taux
    const profA = ctx.createGain()
    profA.gain.value = niveau * r.lfoAmpli.profondeur
    lfoA.connect(profA)
    profA.connect(interne.gain)
    lfoA.start(t)
    sources.push(lfoA)
    noeuds.push(profA)

    if (r.couche) {
      const c = r.couche
      const osc = ctx.createOscillator()
      osc.type = c.type
      osc.frequency.value = c.freq
      const gc = ctx.createGain()
      gc.gain.value = c.gain * NIVEAU_PHASE[this.phase]
      osc.connect(gc)
      gc.connect(externe)
      osc.start(t)
      sources.push(osc)
      noeuds.push(gc)

      const vib = ctx.createOscillator()
      vib.frequency.value = c.vibratoTaux
      const profV = ctx.createGain()
      profV.gain.value = c.vibratoCents
      vib.connect(profV)
      profV.connect(osc.detune)
      vib.start(t)
      sources.push(vib)
      noeuds.push(profV)
    }

    return { externe, niveau, noeuds, sources }
  }

  private detruireNappe(n: Nappe): void {
    for (const s of n.sources) {
      try {
        s.stop()
      } catch {
        // Source deja arretee.
      }
      try {
        s.disconnect()
      } catch {
        // Deja detachee.
      }
    }
    for (const x of n.noeuds) {
      try {
        x.disconnect()
      } catch {
        // Deja detache.
      }
    }
    n.sources.length = 0
    n.noeuds.length = 0
  }

  // ------------------------------------------------------------------- boucle

  maj(dt: number): void {
    const ctx = this.ctx
    if (!ctx) return
    const t = ctx.currentTime

    // Liberer les nappes dont le fondu de sortie est termine.
    for (let i = this.sortantes.length - 1; i >= 0; i--) {
      const s = this.sortantes[i]!
      if (t < s.fin) continue
      this.detruireNappe(s.nappe)
      this.sortantes.splice(i, 1)
    }

    if (this.sourd) return
    this.prochainEvenement -= dt
    if (this.prochainEvenement <= 0) {
      this.evenement()
      this.prochainEvenement = hasard(3.5, 12)
    }
  }

  /** Un petit evenement de vie, choisi selon le biome et l'heure. */
  private evenement(): void {
    const nuit = this.phase === 'nuit'
    const jour = this.phase === 'jour' || this.phase === 'aube'
    const choix: (() => void)[] = []

    switch (this.biome) {
      case 'foret':
        if (jour) choix.push(() => this.oiseau(), () => this.oiseau(), () => this.froissement())
        if (nuit) choix.push(() => this.grillon(), () => this.grillon(), () => this.hulotte())
        if (!jour && !nuit) choix.push(() => this.oiseau(), () => this.grillon())
        break
      case 'plage':
        choix.push(() => this.vague(), () => this.vague())
        if (jour) choix.push(() => this.mouette(), () => this.mouette(), () => this.oiseau())
        if (nuit) choix.push(() => this.grillon())
        break
      case 'montagne':
        choix.push(() => this.rafale())
        if (jour) choix.push(() => this.oiseau())
        if (nuit) choix.push(() => this.hulotte())
        break
      case 'glacier':
        choix.push(() => this.craquementGlace(), () => this.rafale())
        break
      case 'desert':
        choix.push(() => this.rafale())
        if (nuit) choix.push(() => this.grillon(), () => this.grillon())
        break
      /*
       * Les trois biomes de l'ile 2. Aucun son neuf n'a ete ecrit: ce sont les
       * memes generateurs, dans d'autres proportions — c'est ce qui distingue
       * une jungle d'une foret a l'oreille, pas un timbre inedit.
       */
      case 'jungle':
        choix.push(() => this.froissement(), () => this.froissement())
        if (jour) choix.push(() => this.oiseau(), () => this.oiseau(), () => this.oiseau())
        if (nuit) choix.push(() => this.grillon(), () => this.grillon(), () => this.hulotte())
        break
      case 'ferme':
        if (jour) choix.push(() => this.oiseau(), () => this.froissement())
        if (nuit) choix.push(() => this.grillon(), () => this.grillon(), () => this.hulotte())
        if (!jour && !nuit) choix.push(() => this.oiseau(), () => this.grillon())
        break
      case 'cristal':
        // La roche qui craque, empruntee au glacier: dans un canyon de cristal,
        // c'est exactement ce qu'on veut entendre de temps en temps.
        choix.push(() => this.craquementGlace(), () => this.rafale())
        if (nuit) choix.push(() => this.hulotte())
        break
    }
    if (choix.length === 0) return
    unParmi(choix)()
  }

  /** Trille court: deux a cinq notes qui montent puis retombent. */
  private oiseau(): void {
    const v = this.ouvrir(this.busAmbiance)
    if (!v) return
    const t = v.ctx.currentTime + 0.01
    const nb = Math.floor(hasard(2, 6))
    const base = hasard(2100, 3000)
    for (let i = 0; i < nb; i++) {
      const d = t + i * hasard(0.075, 0.15)
      const f = base * hasard(0.9, 1.15)
      this.oscil({
        v,
        debut: d,
        duree: 0.06,
        gain: 0.055,
        type: 'triangle',
        courbe: [
          { t: 0, f },
          { t: 0.022, f: f * 1.45 },
          { t: 0.06, f: f * 1.05 },
        ],
        attaque: 0.006,
      })
    }
  }

  /** Stridulement: des salves de pulsations tres etroites autour de 4,5 kHz. */
  private grillon(): void {
    const v = this.ouvrir(this.busAmbiance)
    if (!v) return
    const t = v.ctx.currentTime + 0.01
    const freq = hasard(4200, 4900)
    for (let s = 0; s < 3; s++) {
      this.grain({
        v,
        debut: t + s * 0.3,
        duree: 0.1,
        gain: 0.05,
        type: 'bandpass',
        freq,
        q: 22,
        attaque: 0.001,
        impulsions: 4,
      })
    }
  }

  /** Hulotte: le "hou" bref puis le "houuu" long et tremble. */
  private hulotte(): void {
    const v = this.ouvrir(this.busAmbiance)
    if (!v) return
    const t = v.ctx.currentTime + 0.01
    const f = hasard(375, 425)
    this.oscil({
      v,
      debut: t,
      duree: 0.26,
      gain: 0.07,
      courbe: [
        { t: 0, f },
        { t: 0.26, f: f * 0.97 },
      ],
      attaque: 0.06,
      coupure: 900,
      vibrato: { taux: 6, cents: 22 },
    })
    this.oscil({
      v,
      debut: t + 0.44,
      duree: 0.7,
      gain: 0.075,
      courbe: [
        { t: 0, f: f * 0.98 },
        { t: 0.12, f: f * 0.93 },
        { t: 0.7, f: f * 0.87 },
      ],
      attaque: 0.13,
      coupure: 820,
      vibrato: { taux: 5.5, cents: 26 },
    })
  }

  /** Mouette: trois cris nasillards qui montent puis cassent. */
  private mouette(): void {
    const v = this.ouvrir(this.busAmbiance)
    if (!v) return
    const t = v.ctx.currentTime + 0.01
    const nb = Math.floor(hasard(2, 4))
    const f = hasard(650, 820)
    for (let i = 0; i < nb; i++) {
      this.oscil({
        v,
        debut: t + i * hasard(0.38, 0.5),
        duree: 0.32,
        gain: 0.06,
        type: 'sawtooth',
        courbe: [
          { t: 0, f },
          { t: 0.06, f: f * 1.6 },
          { t: 0.18, f: f * 1.15 },
          { t: 0.32, f: f * 0.85 },
        ],
        attaque: 0.02,
        coupure: 2600,
        vibrato: { taux: 26, cents: 85 },
      })
    }
  }

  /** Vague: une longue bouffee qui enfle puis se retire, avec de l'ecume. */
  private vague(): void {
    const v = this.ouvrir(this.busAmbiance)
    if (!v) return
    const t = v.ctx.currentTime + 0.01
    this.grain({
      v,
      debut: t,
      duree: 2.4,
      gain: 0.11,
      type: 'lowpass',
      freq: 750,
      freqFin: 300,
      q: 0.8,
      attaque: 0.85,
      rose: true,
    })
    this.grain({ v, debut: t + 0.7, duree: 1.3, gain: 0.05, type: 'bandpass', freq: 1600, q: 0.9, attaque: 0.35 })
  }

  /** Rafale: le vent qui monte d'un cran et redescend. */
  private rafale(): void {
    const v = this.ouvrir(this.busAmbiance)
    if (!v) return
    const t = v.ctx.currentTime + 0.01
    this.grain({
      v,
      debut: t,
      duree: 2.8,
      gain: 0.12,
      type: 'bandpass',
      freq: 550,
      freqFin: 1150,
      q: 0.7,
      attaque: 0.9,
      rose: true,
    })
  }

  /** Craquement de glace: un gemissement grave puis un eclat sec. */
  private craquementGlace(): void {
    const v = this.ouvrir(this.busAmbiance)
    if (!v) return
    const t = v.ctx.currentTime + 0.01
    this.oscil({
      v,
      debut: t,
      duree: 1.1,
      gain: 0.09,
      courbe: [
        { t: 0, f: 62 },
        { t: 1.1, f: 47 },
      ],
      attaque: 0.25,
      coupure: 300,
    })
    this.grain({ v, debut: t + 0.55, duree: 0.14, gain: 0.1, type: 'highpass', freq: 2100, attaque: 0.001, impulsions: 2 })
    this.resonance(v, t + 0.55, { freq: 1250, q: 14, duree: 0.3, gain: 0.045 }, this.busAmbiance ?? undefined)
  }

  /** Froissement dans les fourres: quelque chose bouge, on ne sait pas quoi. */
  private froissement(): void {
    const v = this.ouvrir(this.busAmbiance)
    if (!v) return
    const t = v.ctx.currentTime + 0.01
    this.grain({
      v,
      debut: t,
      duree: 0.6,
      gain: 0.075,
      type: 'bandpass',
      freq: 2500,
      freqFin: 1700,
      q: 1,
      attaque: 0.01,
      impulsions: 7,
    })
  }
}

/** Instance unique partagee par tout le client. */
export const audio = new Audio()
